import {
	ReactElement,
	useEffect,
	useRef,
	Ref,
	useState,
	useCallback,
	KeyboardEvent,
	useMemo
} from "react";
import { HsvColor, hsvToHex } from "client/Misc/colorFunctions";
import styles from "./HsvPicker.module.less";
import {
	clamp,
	isNil,
	resolveFormattedTextString,
	setTrackingId
} from "client/Misc/utils";
import { isTouchEvent } from "client/Misc/domUtils";
import { Key } from "client/Misc/commonTypes";
import { localize } from "client_legacy/ndaw/culture";

type HsvPickerState = {
	hDown: boolean;
	svDown: boolean;
};

export type HsvPickerProps = {
	value: HsvColor | undefined;
	onChange(value: HsvColor): void;
	trackingId?: string;
	onSelectorsKeyUp: (args: KeyboardEvent) => boolean;
};

export const HsvPicker = (props: HsvPickerProps): ReactElement => {
	const {
		value,
		onChange,
		trackingId,
		onSelectorsKeyUp
	} = props;

	const svRef: Ref<HTMLDivElement> = useRef<HTMLDivElement>(null);
	const hRef: Ref<HTMLDivElement> = useRef<HTMLDivElement>(null);

	const [state, setState] = useState<HsvPickerState>({ hDown: false, svDown: false});

	const svSelectorColor: string = value
		? hsvToHex({ ...value, s: 1, v: 1 })!
		: "#ff0000";

	const handleHueEvent = useCallback(
		(x: number, width: number): void => {
			const hue: number = (x / width) * 360;
			const {
				s = 1,
				v = 1
			} = value ?? {};

			onChange({
				h: hue,
				s,
				v
			});
		},
		[value, onChange]
	);

	const handleSaturationValueEvent = useCallback(
		(x: number, y: number, width: number, height: number): void => {
			const s: number = (x / width);
			// The value is inversed
			const v: number = ((height - y) / height);

			const {
				h = 0
			} = value ?? {};

			onChange({ h, s, v });
		},
		[value, onChange]
	);

	const onHClick = (args: React.MouseEvent): void => {
		handleHueEvent(
			args.nativeEvent.offsetX,
			args.currentTarget.clientWidth
		);
	};

	const onSvClick = (args: React.MouseEvent): void => {
		handleSaturationValueEvent(
			args.nativeEvent.offsetX,
			args.nativeEvent.offsetY,
			args.currentTarget.clientWidth,
			args.currentTarget.clientHeight
		);
	};

	const svSelectorKeyUpHandler = useCallback(
		(args: KeyboardEvent): void => {
			const result: boolean = onSelectorsKeyUp(args);
			if (result) {
				args.preventDefault();
				args.stopPropagation();
				return;
			}

			if (!value) {
				return;
			};

			const step = 0.01;
			const { h } = value;
			let { s, v } = value;

			switch (args.key) {
				case Key.ArrowRight:
					s = clamp(s + step, 0, 1);
					break;
				case Key.ArrowLeft:
					s = clamp(s - step, 0, 1);
					break;
				case Key.ArrowUp:
					v = clamp(v + step, 0, 1);
					break;
				case Key.ArrowDown:
					v = clamp(v - step, 0, 1);
					break;
				default:
					return;
			}

			onChange({
				h,
				s,
				v
			});
		},
		[
			onChange,
			onSelectorsKeyUp,
			value
		]
	);

	const hSelectorKeyUpHandler = useCallback(
		(args: KeyboardEvent): void => {
			const result: boolean = onSelectorsKeyUp(args);
			if (result) {
				args.preventDefault();
				args.stopPropagation();
				return;
			}

			if (!value) {
				return;
			};

			const step: number = 360 * 0.01;
			let { h } = value;
			const { s, v } = value;

			switch (args.key) {
				case Key.ArrowRight:
					h = clamp(h + step, 0, 360);
					break;
				case Key.ArrowLeft:
					h = clamp(h - step, 0, 360);
					break;
				default:
					return;
			}

			onChange({
				h,
				s,
				v
			});
		},
		[
			onChange,
			onSelectorsKeyUp,
			value
		]
	);

	useEffect(
		() => {
			const onPointerMove = (args: MouseEvent | TouchEvent) => {
				const { hDown, svDown } = state;

				let clientX;
				let clientY;
				if (isTouchEvent(args)) {
					clientX = args.touches[0].clientX;
					clientY = args.touches[0].clientY;
				} else {
					clientX = args.clientX;
					clientY = args.clientY;
				}

				if (svDown) {
					args.preventDefault();

					const svSelector: HTMLDivElement | null = svRef.current;
					if (!svSelector) {
						return;
					}
					const { x, y, width, height } = svSelector.getBoundingClientRect();

					handleSaturationValueEvent(
						clamp(clientX - x, 0, width),
						clamp(clientY - y, 0, height),
						width,
						height
					);

				} else if (hDown) {
					args.preventDefault();

					const hSelector: HTMLDivElement | null = hRef.current;
					if (!hSelector) {
						return;
					}

					const { x, width } = hSelector.getBoundingClientRect();

					handleHueEvent(
						clamp(clientX - x, 0, width),
						width
					);
				}
			};

			const onPointerUp = () => {
				setState({
					hDown: false,
					svDown: false
				});
			};

			document.addEventListener("mousemove", onPointerMove);
			document.addEventListener("mouseup", onPointerUp);
			document.addEventListener("touchend", onPointerUp);
			document.addEventListener("touchmove", onPointerMove, { passive: false });

			return () => {
				document.removeEventListener("mousemove", onPointerMove);
				document.removeEventListener("mouseup", onPointerUp);
				document.removeEventListener("touchend", onPointerUp);
				document.removeEventListener("touchmove", onPointerMove);
			};
		},
		[
			value,
			state,
			onChange,
			handleHueEvent,
			handleSaturationValueEvent
		]
	);

	const onSvPointerDown = (): void => setState((x) => ({...x, svDown: true}));
	const onHPointerDown = (): void => setState((x) => ({...x, hDown: true}));

	const svValueText = useMemo(
		(): string | undefined => {
			if (isNil(value)) {
				return undefined;
			}

			return resolveFormattedTextString({
				template: localize("_SaturationXBrightnessY"),
				replacements: [
					{
						what: "{{Saturation}}",
						filler: `${Math.round(value.s * 100)}`
					},
					{
						what: "{{Brightness}}",
						filler: `${Math.round(value.v * 100)}`
					}
				]
			});
		},
		[value]
	);

	const hueValueText = useMemo(
		(): string | undefined => {
			if (isNil(value)) {
				return undefined;
			}

			return resolveFormattedTextString({
				template: localize("_HueValueDescription"),
				replacements: [
					{
						what: "{{Value}}",
						filler: `${Math.round(value.h)}`
					}
				]
			});
		},
		[value]
	);

	return (
		<div>
			<div
				{...setTrackingId(trackingId, "__sv")}
				className={styles.svSelector}
				style={{ backgroundColor: svSelectorColor }}
				onClick={onSvClick}
				ref={svRef}
				onMouseDown={onSvPointerDown}
				onTouchStart={onSvPointerDown}
			>
				<div className={styles.sOverlay}>
					<div className={styles.vOverlay}>
					</div>
				</div>
				{ value && (
					<>
						<div
							tabIndex={0}
							className={styles.svIndicator}
							style={{top: `${(1 - value.v) * 100}%`, left: `${value.s * 100}%`}}
							onKeyUp={svSelectorKeyUpHandler}
							role="slider"
							aria-valuetext={svValueText}
							aria-label={localize("_ColorPickerSVControlsDescription")}
						/>
					</>
				)}
			</div>
			<div className={styles.hContainer}>
				<div
					className={styles.hSelector}
					onClick={onHClick}
					ref={hRef}
					onMouseDown={onHPointerDown}
					onTouchStart={onHPointerDown}
					{...setTrackingId(trackingId, "__h")}
				>
					{value && (
						<div
							className={styles.hIndicator}
							tabIndex={0}
							style={{left: `${(value.h / 3.6)}%`}}
							onKeyUp={hSelectorKeyUpHandler}
							role="slider"
							aria-label={localize("_HueUseLeftAndRightArrowKeysToAdjustTheHue")}
							aria-valuenow={value ? Math.round(value.h) : 0}
							aria-valuemin={0}
							aria-valuemax={360}
							aria-orientation="horizontal"
							aria-valuetext={hueValueText}
						/>
					)}
				</div>
			</div>
		</div>
	);
};
HsvPicker.displayName = "HsvPicker";
